@if (help.isOpen()) {
  <aside
    class="help-panel"
    [attr.role]="isMobile() ? 'dialog' : 'complementary'"
    [attr.aria-modal]="isMobile() ? 'true' : null"
    [attr.aria-label]="'help-panel-title' | translate"
    [cdkTrapFocus]="isMobile() && !zoomedImage()"
    [cdkTrapFocusAutoCapture]="isMobile()"
  >
    <header class="help-panel__header">
      @if (help.libraryActive() && !help.showLibrary()) {
        <button
          mat-icon-button
          type="button"
          [matTooltip]="'help-library-back' | translate"
          [attr.aria-label]="'help-library-back' | translate"
          (click)="help.backToLibrary()"
        >
          <i class="fas fa-arrow-left" aria-hidden="true"></i>
        </button>
      } @else {
        <i class="fas fa-book-open" aria-hidden="true"></i>
      }
      <h2 class="help-panel__title">
        {{ help.showLibrary() ? ("help-library-title" | translate) : (guide()?.title ?? ("help-panel-title" | translate)) }}
      </h2>
      <button
        mat-icon-button
        type="button"
        class="help-panel__close"
        cdkFocusInitial
        [matTooltip]="'close' | translate"
        [attr.aria-label]="'close' | translate"
        (click)="help.close()"
      >
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </header>

    <div class="help-panel__body" #body>
      @if (help.showLibrary()) {
        <div class="help-panel__search">
          <input
            type="search"
            class="help-panel__search-input"
            [value]="query()"
            [attr.aria-label]="'help-library-search' | translate"
            [placeholder]="'help-library-search' | translate"
            (input)="query.set($any($event.target).value)"
          />
        </div>
        @if (help.libraryGuides.isLoading()) {
          <p class="help-panel__status" role="status">
            <i class="fas fa-spinner fa-pulse" aria-hidden="true"></i>
            {{ "loading" | translate }}
          </p>
        } @else if (libraryResults().length === 0) {
          <p class="help-panel__status" role="status">{{ "help-library-no-results" | translate }}</p>
        } @else {
          <ul class="help-panel__topics">
            @for (result of libraryResults(); track result.topic.id) {
              <li>
                <button type="button" class="help-panel__toc-link" (click)="help.openFromLibrary(result.topic.id)">
                  <i [class]="result.topic.icon" aria-hidden="true"></i>
                  {{ result.title || ("help-panel-topic." + result.topic.id | translate) }}
                </button>
                @if (result.summary) {
                  <p class="help-panel__library-summary">{{ result.summary }}</p>
                }
                @if (result.sections.length > 0) {
                  <ul>
                    @for (section of result.sections; track section.id) {
                      <li>
                        <button
                          type="button"
                          class="help-panel__toc-link"
                          (click)="help.openFromLibrary(result.topic.id, section.id)"
                        >
                          {{ section.title }}
                        </button>
                      </li>
                    }
                  </ul>
                }
              </li>
            }
          </ul>
        }
      } @else if (help.guide.isLoading()) {
        <p class="help-panel__status" role="status">
          <i class="fas fa-spinner fa-pulse" aria-hidden="true"></i>
          {{ "loading" | translate }}
        </p>
      } @else if (guide(); as current) {
        <p class="help-panel__summary">
          @for (seg of summary(); track $index) {
            @if (seg.strong) {
              <strong>{{ seg.text }}</strong>
            } @else {
              <ng-container>{{ seg.text }}</ng-container>
            }
          }
        </p>

        <nav class="help-panel__toc" [attr.aria-label]="'help-panel-contents' | translate">
          <h3 class="help-panel__toc-title">{{ "help-panel-contents" | translate }}</h3>
          <ol>
            @for (section of sections(); track section.id) {
              <li>
                <button type="button" class="help-panel__toc-link" (click)="scrollToSection(section.id)">
                  {{ section.title }}
                </button>
              </li>
            }
          </ol>
        </nav>

        @for (section of sections(); track section.id) {
          <section class="help-panel__section" tabindex="-1" [attr.data-help-section]="section.id">
            <h3 class="help-panel__section-title">{{ section.title }}</h3>

            @for (block of section.blocks; track $index) {
              @switch (block.kind) {
                @case ("text") {
                  <p>
                    @for (seg of block.segments; track $index) {
                      @if (seg.strong) {
                        <strong>{{ seg.text }}</strong>
                      } @else {
                        <ng-container>{{ seg.text }}</ng-container>
                      }
                    }
                  </p>
                }
                @case ("tip") {
                  <div class="help-panel__callout help-panel__callout--tip">
                    <i class="fas fa-lightbulb" aria-hidden="true"></i>
                    <p>
                      @for (seg of block.segments; track $index) {
                        @if (seg.strong) {
                          <strong>{{ seg.text }}</strong>
                        } @else {
                          <ng-container>{{ seg.text }}</ng-container>
                        }
                      }
                    </p>
                  </div>
                }
                @case ("warning") {
                  <div class="help-panel__callout help-panel__callout--warning" role="note">
                    <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                    <p>
                      @for (seg of block.segments; track $index) {
                        @if (seg.strong) {
                          <strong>{{ seg.text }}</strong>
                        } @else {
                          <ng-container>{{ seg.text }}</ng-container>
                        }
                      }
                    </p>
                  </div>
                }
                @case ("steps") {
                  <ol class="help-panel__steps">
                    @for (item of block.items; track $index) {
                      <li>
                        @for (seg of item; track $index) {
                          @if (seg.strong) {
                            <strong>{{ seg.text }}</strong>
                          } @else {
                            <ng-container>{{ seg.text }}</ng-container>
                          }
                        }
                      </li>
                    }
                  </ol>
                }
                @case ("list") {
                  <ul class="help-panel__list">
                    @for (item of block.items; track $index) {
                      <li>
                        @for (seg of item; track $index) {
                          @if (seg.strong) {
                            <strong>{{ seg.text }}</strong>
                          } @else {
                            <ng-container>{{ seg.text }}</ng-container>
                          }
                        }
                      </li>
                    }
                  </ul>
                }
                @case ("example") {
                  <div class="help-panel__example">
                    <h4>
                      <i class="fas fa-flask" aria-hidden="true"></i>
                      <span>{{ block.title }}</span>
                    </h4>
                    <ul>
                      @for (item of block.items; track $index) {
                        <li>
                          @for (seg of item; track $index) {
                            @if (seg.strong) {
                              <strong>{{ seg.text }}</strong>
                            } @else {
                              <ng-container>{{ seg.text }}</ng-container>
                            }
                          }
                        </li>
                      }
                    </ul>
                  </div>
                }
                @case ("faq") {
                  <div class="help-panel__faq">
                    @for (item of block.items; track $index) {
                      <details>
                        <summary>{{ item.q }}</summary>
                        <p>
                          @for (seg of item.a; track $index) {
                            @if (seg.strong) {
                              <strong>{{ seg.text }}</strong>
                            } @else {
                              <ng-container>{{ seg.text }}</ng-container>
                            }
                          }
                        </p>
                      </details>
                    }
                  </div>
                }
                @case ("image") {
                  <figure class="help-panel__figure">
                    @if (missingImages().has(block.url)) {
                      <div class="help-panel__image-placeholder" role="img" [attr.aria-label]="block.alt">
                        <i class="fas fa-image" aria-hidden="true"></i>
                        <span>{{ "help-panel-image-unavailable" | translate }}</span>
                      </div>
                    } @else {
                      <button
                        type="button"
                        class="help-panel__image-button"
                        [attr.aria-label]="('help-panel-zoom' | translate) + ': ' + block.alt"
                        (click)="zoomedImage.set({ url: block.url, alt: block.alt })"
                      >
                        <img
                          [src]="block.url"
                          [alt]="block.alt"
                          loading="lazy"
                          decoding="async"
                          (error)="markImageMissing(block.url)"
                        />
                      </button>
                    }
                    @if (block.caption) {
                      <figcaption>{{ block.caption }}</figcaption>
                    }
                  </figure>
                }
                @case ("video") {
                  @defer (on viewport) {
                    <app-help-video
                      [mediaId]="block.mediaId"
                      [title]="block.title"
                      [caption]="block.caption"
                      [localSrc]="block.localSrc"
                      [lang]="lang()"
                      [media]="media()"
                    ></app-help-video>
                  } @placeholder {
                    <div class="help-panel__video-placeholder"></div>
                  }
                }
              }
            }

          </section>
        }
      } @else if (help.topic()) {
        <p class="help-panel__status" role="alert">{{ "help-panel-load-error" | translate }}</p>
      } @else {
        <p class="help-panel__status">{{ "help-panel-no-guide" | translate }}</p>
        @if (availableTopics().length > 0) {
          <h3 class="help-panel__toc-title">{{ "help-panel-available" | translate }}</h3>
          <ul class="help-panel__topics">
            @for (topic of availableTopics(); track topic.id) {
              <li>
                <button type="button" class="help-panel__toc-link" (click)="help.open(topic.id)">
                  <i [class]="topic.icon" aria-hidden="true"></i>
                  {{ "help-panel-topic." + topic.id | translate }}
                </button>
              </li>
            }
          </ul>
        }
      }
    </div>

  </aside>

  @if (zoomedImage(); as image) {
    <div
      class="help-panel__lightbox"
      role="dialog"
      aria-modal="true"
      cdkTrapFocus
      cdkTrapFocusAutoCapture
      [attr.aria-label]="image.alt"
      (click)="zoomedImage.set(null)"
    >
      <img [src]="image.url" [alt]="image.alt" />
      <button
        mat-icon-button
        type="button"
        class="help-panel__lightbox-close"
        cdkFocusInitial
        [attr.aria-label]="'close' | translate"
        (click)="zoomedImage.set(null)"
      >
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </div>
  }
}
